// Client de l'API du Worker. Les cookies de session partent automatiquement (même origine).
import type { StoredSecurityAssessment } from "../src/security-assessment";
import type { SendingAlias } from "./aliases";
import { clearSecurityAssessmentCache } from "./security-cache";

export type { SendingAlias };

export interface Mailbox { name: string | null; address: string }

export interface Message {
  id: string;
  threadId: string;
  address: string;
  location: string;
  from: Mailbox;
  to: Mailbox[];
  cc: Mailbox[];
  subject: string | null;
  snippet: string | null;
  receivedAt: number;
  size: number;
  hasAttachments: boolean | null;
  read: boolean;
  starred: boolean;
  forwardedAt: number | null;
  // Authenticité de l'expéditeur vérifiée à la réception (null : inconnue).
  auth: "pass" | "fail" | null;
}

export interface ThreadSummary {
  id: string;
  count: number;
  unread: number;
  starred: boolean;
  hasAttachments: boolean;
  lastAt: number;
  latest: Message;
}

export interface Count { total: number; unread: number }

// Place dans la barre latérale : normale, épinglée en haut, ou repliée dans « Masqués ».
export type Place = "normal" | "pinned" | "hidden";


export interface Folders {
  addresses: ({ address: string; place: Place; notify: boolean } & Count)[];
  aliases?: SendingAlias[]; // absent d'une liste mise en cache avant leur ajout
  system: Record<string, Count>;
  custom: ({ id: string; name: string; place: Place } & Count)[];
  drafts: number;
}

export interface DraftAttachment { id: string; filename: string; mimeType: string; size: number }

export interface Draft {
  id: string;
  kind: "new" | "reply" | "forward";
  from: string | null;
  to: Mailbox[];
  cc: Mailbox[];
  bcc: Mailbox[];
  subject: string;
  html: string;
  text: string;
  quoteHtml: string;
  quoteText: string;
  inReplyTo: string | null;
  references: string | null;
  threadId: string | null;
  updatedAt: number;
  attachments: DraftAttachment[];
}

export type DraftFields = Partial<Omit<Draft, "id" | "updatedAt" | "attachments">>;

export interface DraftSummary {
  id: string;
  kind: Draft["kind"];
  from: string | null;
  to: Mailbox[];
  subject: string;
  snippet: string;
  updatedAt: number;
}

export interface RuleCondition { field: "from" | "to" | "subject"; op: "contains" | "is"; value: string }
export interface Rule {
  id: string;
  name: string;
  enabled: boolean;
  conditions: RuleCondition[];
  actions: { moveTo?: string; markRead?: boolean; star?: boolean };
}

export interface OutOfOffice {
  enabled: boolean;
  from: number | null;
  until: number | null;
  subject: string;
  text: string;
  addresses: string[];
}

// Accès IA : clés d'autres fournisseurs, serveurs locaux, jetons ChatGPT ou activation
// de Claude (enabled seulement ; sa connexion reste dans le CLI sur chaque appareil).
export interface AiCredential {
  key?: string;
  baseUrl?: string;
  enabled?: boolean;
  access?: string;
  refresh?: string;
  expires?: number;
  accountId?: string;
  email?: string;
  plan?: string;
}

export interface AiConversationSummary { id: string; messageId: string; title: string | null; model: string | null; createdAt: number; updatedAt: number }
export interface CleanupReceipt { ids: string[]; skipped: string[]; trashedAt: number }

export class Unauthorized extends Error {}

// Copie hors ligne des e-mails (voir public/sw.js) : effacée dès que la session prend fin.
export const forgetOfflineCopy = () => {
  clearSecurityAssessmentCache();
  return globalThis.caches?.delete("data-v1").catch(() => false);
};
export class Offline extends Error {}

// Au-delà, le réseau est considéré comme coupé : mieux vaut un message qu'une attente sans fin.
const TIMEOUT_MS = 20_000;

async function send(path: string, init: RequestInit = {}, timeout = TIMEOUT_MS): Promise<Response> {
  try {
    const signal = init.signal ? AbortSignal.any([init.signal, AbortSignal.timeout(timeout)]) : AbortSignal.timeout(timeout);
    return await fetch(`/api${path}`, { ...init, signal });
  } catch (e) {
    if (init.signal?.aborted) throw e;
    throw new Offline("Pas de connexion au serveur. Vérifiez le réseau puis réessayez.");
  }
}

async function request<T>(method: string, path: string, body?: unknown, timeout?: number, signal?: AbortSignal): Promise<T> {
  const res = await send(path, {
    method,
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  }, timeout);
  // Réponse tirée de la copie hors ligne du service worker : la web app affiche un bandeau.
  document.documentElement.toggleAttribute("data-offline", res.headers.get("X-Offline") === "1");
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }));
    const message = (error as { error?: string }).error ?? `Erreur ${res.status}`;
    // 401 : session absente ou expirée, ou identifiants refusés à la connexion.
    throw res.status === 401 ? new Unauthorized(message) : new Error(message);
  }
  return res.json() as Promise<T>;
}

const q = (params: Record<string, string | number | undefined>) => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined) as [string, string][];
  return entries.length ? `?${new URLSearchParams(entries)}` : "";
};

export const api = {
  me: () => request<{ authenticated: boolean; passkeys: number }>("GET", "/auth/me"),
  loginPassword: (password: string) => request<{ ok: true }>("POST", "/auth/password", { password }),
  loginPasskeyOptions: () => request<any>("POST", "/auth/passkey/login/options", {}),
  loginPasskeyVerify: (response: unknown) => request<{ ok: true }>("POST", "/auth/passkey/login/verify", response),
  registerPasskeyOptions: () => request<any>("POST", "/auth/passkey/register/options", {}),
  registerPasskeyVerify: (response: unknown) => request<{ ok: true }>("POST", "/auth/passkey/register/verify", response),
  passkeys: () => request<{ id: string; device: string | null; created_at: number; last_used: number | null }[]>("GET", "/auth/passkeys"),
  deletePasskey: (id: string) => request("DELETE", `/auth/passkeys/${encodeURIComponent(id)}`),
  sessions: () => request<{ id: string; device: string | null; created_at: number; last_seen: number; current: boolean }[]>("GET", "/auth/sessions"),
  deleteSession: (id: string) => request("DELETE", `/auth/sessions/${encodeURIComponent(id)}`),
  logout: () => request("POST", "/auth/logout", {}),

  folders: () => request<Folders>("GET", "/folders"),
  threads: (location: string, address?: string, before?: number) =>
    request<{ threads: ThreadSummary[]; next: number | null }>("GET", `/threads${q({ location, address, before })}`),
  thread: (id: string, location?: string) =>
    request<{ id: string; messages: Message[] }>("GET", `/threads/${encodeURIComponent(id)}${q({ location: location === "trash" ? "trash" : undefined })}`),
  raw: async (id: string, signal?: AbortSignal): Promise<ArrayBuffer> => {
    const res = await send(`/messages/${encodeURIComponent(id)}/raw`, { signal });
    if (res.status === 401) throw new Unauthorized();
    if (!res.ok) throw new Error("E-mail introuvable");
    return res.arrayBuffer();
  },
  index: (id: string, text: string, hasAttachments: boolean) =>
    request("POST", `/messages/${encodeURIComponent(id)}/index`, { text, hasAttachments }),
  update: (ids: string[], change: { read?: boolean; starred?: boolean; location?: string }) =>
    request<{ updated: number }>("PATCH", "/messages", { ids, ...change }),
  forward: (id: string) => request<{ forwardedAt: number }>("POST", `/messages/${encodeURIComponent(id)}/forward`, {}),
  // Sélection multiple : conversations entières, limitées au dossier affiché (from).
  updateThreads: (threads: string[], from: string, change: { read?: boolean; starred?: boolean; location?: string }) =>
    request<{ updated: number; ids: string[] }>("PATCH", "/threads", { threads, from, ...change }),
  deleteThreads: (threads: string[]) => request<{ deleted: number }>("DELETE", "/threads", { threads }),
  // Tout un dossier d'un coup (celui d'une adresse : location inbox + address).
  updateFolder: (folder: { location: string; address?: string }, change: { read: boolean } | { to: string }) =>
    request<{ updated: number }>("PATCH", "/folder", { ...folder, ...change }),
  setAddressPlace: (address: string, place: Place) => request("PUT", `/addresses/${encodeURIComponent(address)}/place`, { place }),
  setFolderPlace: (id: string, place: Place) => request("PUT", `/custom-folders/${encodeURIComponent(id)}/place`, { place }),
  emptyTrash: () => request<{ deleted: number; remaining: number }>("DELETE", "/trash", {}),
  deleteForever: (ids: string[]) => request<{ deleted: number }>("DELETE", "/messages", { ids }),
  search: (query: string, location?: string) => request<{ results: Message[] }>("GET", `/search${q({ q: query, location })}`),
  drafts: () => request<{ drafts: DraftSummary[] }>("GET", "/drafts"),
  draft: (id: string) => request<Draft>("GET", `/drafts/${encodeURIComponent(id)}`),
  createDraft: (fields: DraftFields) => request<Draft>("POST", "/drafts", fields),
  saveDraft: (id: string, fields: DraftFields) => request<{ updatedAt: number }>("PUT", `/drafts/${encodeURIComponent(id)}`, fields),
  deleteDraft: (id: string) => request("DELETE", `/drafts/${encodeURIComponent(id)}`),
  // Avec 10 Mo de pièces jointes, le relais vers Resend peut prendre plusieurs secondes.
  sendDraft: (id: string) => request<{ id: string; threadId: string }>("POST", `/drafts/${encodeURIComponent(id)}/send`, {}, 120_000),
  // Le contenu part en base64 sur une seule ligne : le Worker le range tel quel dans R2.
  addAttachment: async (draftId: string, name: string, type: string, size: number, base64: string): Promise<DraftAttachment> => {
    const res = await send(`/drafts/${encodeURIComponent(draftId)}/attachments`, {
      method: "POST",
      headers: {
        "Content-Type": "text/plain",
        "X-Filename": encodeURIComponent(name),
        "X-Mime-Type": type || "application/octet-stream",
        "X-Size": String(size),
      },
      body: base64,
    }, 300_000); // un envoi de 10 Mo sur un réseau mobile lent
    if (res.status === 401) throw new Unauthorized();
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? `Erreur ${res.status}`);
    return res.json();
  },
  deleteAttachment: (draftId: string, id: string) =>
    request("DELETE", `/drafts/${encodeURIComponent(draftId)}/attachments/${encodeURIComponent(id)}`),
  contacts: (text: string) => request<{ contacts: Mailbox[] }>("GET", `/contacts${q({ q: text })}`),
  preferences: () => request<{ displayName: string; signatures: { address: string; text: string }[]; aliases: SendingAlias[] }>("GET", "/preferences"),
  setAlias: (alias: string, address: string, name: string) => request<SendingAlias>("PUT", `/aliases/${encodeURIComponent(alias)}`, { address, name }),
  deleteAlias: (alias: string) => request("DELETE", `/aliases/${encodeURIComponent(alias)}`),
  setDisplayName: (displayName: string) => request<{ displayName: string }>("PUT", "/preferences", { displayName }),
  setSignature: (address: string, text: string) => request("PUT", `/signatures/${encodeURIComponent(address)}`, { text }),
  rules: () => request<{ rules: Rule[] }>("GET", "/rules"),
  createRule: (rule: Omit<Rule, "id">) => request<Rule>("POST", "/rules", rule),
  updateRule: (id: string, rule: Omit<Rule, "id">) => request<Rule>("PUT", `/rules/${encodeURIComponent(id)}`, rule),
  deleteRule: (id: string) => request("DELETE", `/rules/${encodeURIComponent(id)}`),
  applyRule: (id: string) => request<{ updated: number }>("POST", `/rules/${encodeURIComponent(id)}/apply`, {}),
  // Assistant IA : accès aux fournisseurs (déchiffrés par le Worker), modèle par défaut,
  // conversations rattachées à un e-mail.
  aiCredentials: () => request<{ providers: Record<string, AiCredential> }>("GET", "/ai/credentials"),
  setAiCredential: (provider: string, data: AiCredential) => request("PUT", `/ai/credentials/${encodeURIComponent(provider)}`, data),
  deleteAiCredential: (provider: string) => request("DELETE", `/ai/credentials/${encodeURIComponent(provider)}`),
  aiSettings: () => request<{ model: string | null }>("GET", "/ai/settings"),
  aiSecurityAnalysis: (id: string) => request<{ analysis: StoredSecurityAssessment | null }>("GET", "/ai/security/" + encodeURIComponent(id)),
  saveAiSecurityAnalysis: (id: string, analysis: Omit<StoredSecurityAssessment, "updatedAt">) =>
    request<{ updatedAt: number }>("PUT", "/ai/security/" + encodeURIComponent(id), analysis),
  inboxAiMessages: (before?: string, signal?: AbortSignal) =>
    request<{ messages: Message[]; total: number; next: string | null }>("GET", "/ai/inbox" + q({ before }), undefined, undefined, signal),
  cleanupInbox: (entries: { id: string; read: boolean }[]) =>
    request<CleanupReceipt>("POST", "/ai/inbox/cleanup", { entries }),
  restoreInbox: (receipt: Pick<CleanupReceipt, "ids" | "trashedAt">) =>
    request<{ ids: string[] }>("POST", "/ai/inbox/restore", receipt),
  setAiSettings: (model: string) => request("PUT", "/ai/settings", { model }),
  aiConversations: (messageId: string) => request<{ conversations: AiConversationSummary[] }>("GET", `/ai/conversations${q({ message: messageId })}`),
  aiConversation: (id: string) => request<AiConversationSummary & { messages: unknown[] }>("GET", `/ai/conversations/${encodeURIComponent(id)}`),
  saveAiConversation: (id: string, body: { messageId: string; title: string; model: string; messages: unknown[] }) =>
    request<{ id: string; updatedAt: number }>("PUT", `/ai/conversations/${encodeURIComponent(id)}`, body),
  deleteAiConversation: (id: string) => request("DELETE", `/ai/conversations/${encodeURIComponent(id)}`),

  push: () => request<{ publicKey: string | null; addresses: { address: string; notify: boolean; hidden: boolean }[] }>("GET", "/push"),
  setNotify: (address: string, notify: boolean) => request("PUT", `/push/addresses/${encodeURIComponent(address)}`, { notify }),
  subscribePush: (subscription: PushSubscriptionJSON) => request("POST", "/push/subscriptions", subscription),
  unsubscribePush: (endpoint: string) => request("DELETE", "/push/subscriptions", { endpoint }),
  testPush: () => request<{ sent: number; failed: number }>("POST", "/push/test", {}),
  outOfOffice: () => request<OutOfOffice>("GET", "/out-of-office"),
  setOutOfOffice: (ooo: OutOfOffice) => request<OutOfOffice>("PUT", "/out-of-office", ooo),
  createFolder: (name: string) => request<{ id: string; name: string }>("POST", "/custom-folders", { name }),
  renameFolder: (id: string, name: string) => request("PATCH", `/custom-folders/${encodeURIComponent(id)}`, { name }),
  deleteFolder: (id: string) => request("DELETE", `/custom-folders/${encodeURIComponent(id)}`),
};
